import {environment} from '@env/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DateTime} from 'luxon';
import {format} from 'date-fns';

export interface Page<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
}

export interface Be3Page<T> {
  content: T[];
  currentPage: number;
  pageSize: number;
  totalElementsCount: number;
}

export interface Pageable {
    page?: number;
    size?: number;
    sort?: Sort;
}

export interface Sort {
    column: string;
    direction: 'ASC' | 'DESC';
}

type QueryString = HttpParams | {
    [p: string]: string | number | boolean | ReadonlyArray<string | number | boolean>
};

export function httpParams(params: object) {
    let maps = new HttpParams();
    Object.keys(params).forEach(key => {
        const value = params[key];
        if (value) {
            if (value instanceof Date) {
                maps = maps.set(key, format(value, 'yyyy-MM-dd'));
                return;
            } else {
                maps = maps.set(key, value);
            }
        }
    });
    return maps;
}

export class Be3Repository<S, C = S> {
    protected baseURL = environment.api3Url;

    protected entityUrl: string;

    constructor(protected http: HttpClient, protected entity: string) {
        if (entity) {
            this.entityUrl = `${this.baseURL}/api/internal/${this.entity}`;
        }
    }

    protected mapEntity() {
        return map((rs: any) => this.fromDTO(rs));
    }

    protected fromDTO(entity: S): C {
        return entity as unknown as C;
    }

    protected toDTO(entity: Partial<C>): S {
        return entity as unknown as S;
    }

    create(model: Partial<C>): Observable<C> {
        return this.http.post<any>(this.entityUrl, this.toDTO(model)).pipe(this.mapEntity());
    }

    update(model: C): Observable<C> {
        return this.http.put<any>(`${this.entityUrl}/${model['id']}`, this.toDTO(model)).pipe(this.mapEntity());
    }

    updatePartial(model: Partial<C>): Observable<C> {
        return this.http.put<any>(`${this.entityUrl}/partial/${model['id']}`, model).pipe(this.mapEntity());
    }

    get(id: string): Observable<C> {
        return this.http.get<any>(`${this.entityUrl}/${id}`).pipe(this.mapEntity());
    }

    delete(id: string): Observable<void> {
        return this.http.delete<any>(`${this.entityUrl}/${id}`);
    }

    getAll(page?: Pageable): Observable<Page<S>> {
        const params = {
            page: `${page?.page ?? 0}`,
            size: `${page?.size ?? 100}`,
            sort: page?.sort ? `${page?.sort.column},${page?.sort.direction}` : ''
        };
        return this.http.get<Page<S>>(`${this.entityUrl}/list`, {params});
    }

    protected searchAsList(params: QueryString): Observable<S[]> {
        return this.http.get<S[]>(`${this.entityUrl}/search/`, {params});
    }

    searchAsPage(
      page: number,
      size: number,
      sortField?: string,
      sortOrder?: string,
      filters?: { [key: string]: string }
    ): Observable<Be3Page<S>> {
      let params = new HttpParams()
        .set('page', page)
        .set('size', size);

      if (sortField) {
        params = params.set('sortField', sortField).set('sortOrder', sortOrder || 'asc');
      }

      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value?.trim()) {
          params = params.set(key, value);
        }
      });

      return this.http.get<Be3Page<S>>(`${this.entityUrl}/searchPage/`, { params });
    }
}


